import { Injectable, inject, signal, Injector } from '@angular/core';
import { FikoYTRequestService } from '@/src/app/shared/services/fikoyt-request.service';
import { FikoYTService } from '@/src/service/fikoyt.service';

const VT = 'FikoYT.sistem.tetikleme-kurallari';
const VT_LOG = 'FikoYT.sistem.tetikleme-loglari';
const DB_AGGREGATE = 'FikoYT.sistem.DB.hareketler.aggregate';
const DB_EKLE = 'FikoYT.sistem.DB.hareketler.ekle';
const DB_GUNCELLE = 'FikoYT.sistem.DB.hareketler.guncelle';
const DB_SIL = 'FikoYT.sistem.DB.hareketler.sil';

export type TetikOlay = 'olusturuldu' | 'guncellendi' | 'silindi' | 'alt_kart_eklendi' | '*';
export type TetikOperator = '=' | '!=' | '>' | '<' | 'icerir' | 'degisti';
export type AksiyonTipi = 'bildirim' | 'mail' | 'push' | 'webhook' | 'otomasyon';
export type AksiyonHedef = 'sahip' | 'olusturan' | 'belirli_kullanici' | 'rol_bazli';
export type AksiyonOncelik = 'acil' | 'normal' | 'dusuk';

export interface ITetiklemeKosul {
  bilesenTipi: string;
  olay: TetikOlay;
  alan?: string;
  operator?: TetikOperator;
  deger?: any;
}

export interface ITetiklemeAksiyon {
  tip: AksiyonTipi;
  hedef?: AksiyonHedef;
  hedefKullaniciId?: string;
  hedefRol?: string;
  mailKonu?: string;
  mailIcerik?: string;
  webhookUrl?: string;
  webhookMetod?: 'GET' | 'POST';
  mesaj?: string;
  oncelik?: AksiyonOncelik;
}

export interface ITetiklemeKurali {
  _id?: string;
  vt: typeof VT;
  ismi: string;
  aciklama?: string;
  aktif: boolean;
  kosul: ITetiklemeKosul;
  aksiyonlar: ITetiklemeAksiyon[];
  olusturan: string;
  tarih: string;
}

export const HAZIR_SABLONLAR: Omit<ITetiklemeKurali, '_id' | 'vt' | 'olusturan' | 'tarih'>[] = [
  {
    ismi: 'Acil Bakim Bildirimi',
    aciklama: 'Bakim talebi aciliyet=Yuksek oldugunda yoneticiye bildir',
    aktif: true,
    kosul: { bilesenTipi: 'bakim-talebi', olay: 'olusturuldu', alan: 'aciliyet', operator: '=', deger: 'Yuksek' },
    aksiyonlar: [{ tip: 'bildirim', hedef: 'rol_bazli', hedefRol: 'yonetici', mesaj: 'Acil bakim talebi olusturuldu!', oncelik: 'acil' }],
  },
  {
    ismi: 'Siparis Bildirimi',
    aciklama: 'Yeni siparis olusturuldugunda muhasebeciye bildir',
    aktif: true,
    kosul: { bilesenTipi: 'siparis', olay: 'olusturuldu' },
    aksiyonlar: [{ tip: 'bildirim', hedef: 'rol_bazli', hedefRol: 'muhasebeci', mesaj: 'Yeni siparis olusturuldu' }],
  },
  {
    ismi: 'Makine Ariza Bildirimi',
    aciklama: 'Makine durumu Arizali oldugunda mail gonder',
    aktif: true,
    kosul: { bilesenTipi: 'makine-tanimi', olay: 'guncellendi', alan: 'durum', operator: '=', deger: 'Arizali' },
    aksiyonlar: [{ tip: 'mail', hedef: 'rol_bazli', hedefRol: 'yonetici', mailKonu: 'Makine Arizasi!', mailIcerik: 'Bir makine arizali duruma gecti.' }],
  },
  {
    ismi: 'Kütük Atama Bildirimi',
    aciklama: 'Kütük oluşturulduğunda atanan kütükçüye bildir',
    aktif: true,
    kosul: { bilesenTipi: 'cam-kutuk', olay: 'olusturuldu' },
    aksiyonlar: [{ tip: 'bildirim', hedef: 'sahip', mesaj: 'Size yeni bir kütük atandı', oncelik: 'normal' }, { tip: 'push', hedef: 'sahip', mesaj: 'Yeni kütük talebi' }],
  },
  {
    ismi: 'Kütük Tamamlandı Bildirimi',
    aciklama: 'Kütük tamamlandığında proje sahibine ve CNC operatörüne bildir',
    aktif: true,
    kosul: { bilesenTipi: 'cam-kutuk', olay: 'guncellendi', alan: 'durum', operator: '=', deger: 'Tamamlandı' },
    aksiyonlar: [{ tip: 'bildirim', hedef: 'sahip', mesaj: 'Kütük hazırlandı, CNC işleme hazır', oncelik: 'normal' }],
  },
  {
    ismi: 'CNC İşlem Tamamlandı',
    aciklama: 'CNC işlemi tamamlandığında proje sahibine bildir',
    aktif: true,
    kosul: { bilesenTipi: 'cam-cnc-islem', olay: 'guncellendi', alan: 'durum', operator: '=', deger: 'Tamamlandı' },
    aksiyonlar: [{ tip: 'bildirim', hedef: 'sahip', mesaj: 'CNC işlemi tamamlandı', oncelik: 'normal' }],
  },
];

export const BILESEN_TIPLERI = [
  { deger: '*', etiket: 'Tumu' },
  { deger: 'bakim-talebi', etiket: 'Bakim Talebi' },
  { deger: 'siparis', etiket: 'Siparis' },
  { deger: 'personel', etiket: 'Personel' },
  { deger: 'makine-tanimi', etiket: 'Makine Tanimi' },
  { deger: 'kalite-kontrol', etiket: 'Kalite Kontrol' },
  { deger: 'stok', etiket: 'Stok' },
  { deger: 'proje', etiket: 'Proje' },
  { deger: 'cam-parca', etiket: 'Cam Parça' },
  { deger: 'cam-kutuk', etiket: 'Cam Kütük' },
  { deger: 'cam-cnc-islem', etiket: 'CNC İşlem' },
];

export const OLAYLAR: { deger: TetikOlay; etiket: string }[] = [
  { deger: '*', etiket: 'Tumu' },
  { deger: 'olusturuldu', etiket: 'Olusturuldu' },
  { deger: 'guncellendi', etiket: 'Guncellendi' },
  { deger: 'silindi', etiket: 'Silindi' },
  { deger: 'alt_kart_eklendi', etiket: 'Alt Kart Eklendi' },
];

export const OPERATORLER: { deger: TetikOperator; etiket: string }[] = [
  { deger: '=', etiket: 'Esit (=)' },
  { deger: '!=', etiket: 'Esit Degil (!=)' },
  { deger: '>', etiket: 'Buyuk (>)' },
  { deger: '<', etiket: 'Kucuk (<)' },
  { deger: 'icerir', etiket: 'Icerir' },
  { deger: 'degisti', etiket: 'Degisti' },
];

@Injectable({ providedIn: 'root' })
export class TetiklemeService {
  private req = inject(FikoYTRequestService);
  private fikoYT = inject(FikoYTService);
  private injector = inject(Injector);

  kurallar = signal<ITetiklemeKurali[]>([]);
  yukleniyor = signal(false);

  async kurallariYukle(): Promise<void> {
    this.yukleniyor.set(true);
    try {
      const sonuc = await this.req.aggregate<ITetiklemeKurali>(DB_AGGREGATE, [
        { $match: { vt: VT } },
        { $sort: { tarih: -1 } },
        { $limit: 200 },
      ]);
      this.kurallar.set(sonuc);
    } finally {
      this.yukleniyor.set(false);
    }
  }

  async kuralKaydet(kural: Partial<ITetiklemeKurali>): Promise<void> {
    const kullanici = this.fikoYT.kimlik.aktifKullaniciJson();
    if (kural._id) {
      await this.req.guncelle(DB_GUNCELLE,
        { _id: kural._id, vt: VT },
        { $set: { ...kural, vt: VT } },
      );
    } else {
      await this.req.ekle(DB_EKLE, {
        ...kural,
        vt: VT,
        olusturan: kullanici?._id || '',
        tarih: new Date().toISOString(),
      });
    }
    await this.kurallariYukle();
  }

  async kuralSil(kuralId: string): Promise<void> {
    await this.req.sil(DB_SIL, { _id: kuralId, vt: VT });
    this.kurallar.update(l => l.filter(k => k._id !== kuralId));
  }

  async aktiflikDegistir(kural: ITetiklemeKurali): Promise<void> {
    await this.req.guncelle(DB_GUNCELLE,
      { _id: kural._id, vt: VT },
      { $set: { aktif: kural.aktif } },
    );
  }

  async tetiklemeleriKontrolEt(olay: string, bilesenTipi: string, kartVerisi: any): Promise<void> {
    const aktifKurallar = this.kurallar().filter(k => k.aktif);
    for (const kural of aktifKurallar) {
      if (this.kosulUyuyorMu(kural, olay, bilesenTipi, kartVerisi)) {
        try {
          await this.aksiyonlariCalistir(kural, kartVerisi);
          await this.tetiklemeLoguKaydet(kural, olay, bilesenTipi, 'basarili');
        } catch (e: any) {
          await this.tetiklemeLoguKaydet(kural, olay, bilesenTipi, 'basarisiz', e?.message || 'Aksiyon hatasi');
        }
      }
    }
  }

  private async tetiklemeLoguKaydet(
    kural: ITetiklemeKurali, olay: string, bilesenTipi: string,
    sonuc: 'basarili' | 'basarisiz' | 'atlandi', detay?: string,
  ) {
    try {
      await this.req.ekle(DB_EKLE, {
        vt: VT_LOG,
        kuralId: kural._id,
        kuralIsmi: kural.ismi,
        olay,
        bilesenTipi,
        sonuc,
        detay: detay || '',
        aksiyonSayisi: kural.aksiyonlar?.length || 0,
        tarih: new Date().toISOString(),
      });
    } catch {}
  }

  async loglariGetir(filtre?: { sonuc?: string; kuralId?: string; tarih?: Date }): Promise<any[]> {
    const match: any = { vt: VT_LOG };
    if (filtre?.sonuc) match.sonuc = filtre.sonuc;
    if (filtre?.kuralId) match.kuralId = filtre.kuralId;
    if (filtre?.tarih) {
      const gun = new Date(filtre.tarih);
      gun.setHours(0, 0, 0, 0);
      const sonraki = new Date(gun);
      sonraki.setDate(sonraki.getDate() + 1);
      match.tarih = { $gte: gun.toISOString(), $lt: sonraki.toISOString() };
    }
    return this.req.aggregate(DB_AGGREGATE, [
      { $match: match },
      { $sort: { tarih: -1 } },
      { $limit: 200 },
    ]);
  }

  async logIstatistikleriGetir(): Promise<{ bugunTetiklenen: number; basarili: number; basarisiz: number; sonTetikleme: string | null }> {
    const bugun = new Date();
    bugun.setHours(0, 0, 0, 0);
    const yarin = new Date(bugun);
    yarin.setDate(yarin.getDate() + 1);

    const loglar = await this.req.aggregate<any>(DB_AGGREGATE, [
      { $match: { vt: VT_LOG, tarih: { $gte: bugun.toISOString(), $lt: yarin.toISOString() } } },
      { $sort: { tarih: -1 } },
    ]);

    return {
      bugunTetiklenen: loglar.length,
      basarili: loglar.filter((l: any) => l.sonuc === 'basarili').length,
      basarisiz: loglar.filter((l: any) => l.sonuc === 'basarisiz').length,
      sonTetikleme: loglar[0]?.tarih || null,
    };
  }

  async loglariTemizle(): Promise<void> {
    await this.req.sil(DB_SIL, { vt: VT_LOG });
  }

  async kuralLoglariniGetir(kuralId: string, limit = 10): Promise<any[]> {
    return this.req.aggregate(DB_AGGREGATE, [
      { $match: { vt: VT_LOG, kuralId } },
      { $sort: { tarih: -1 } },
      { $limit: limit },
    ]);
  }

  private kosulUyuyorMu(kural: ITetiklemeKurali, olay: string, bilesenTipi: string, veri: any): boolean {
    const k = kural.kosul;
    if (k.bilesenTipi !== '*' && k.bilesenTipi !== bilesenTipi) return false;
    if (k.olay !== '*' && k.olay !== olay) return false;
    if (k.alan && k.alan !== '*') {
      const deger = veri?.ek?.[k.alan];
      switch (k.operator) {
        case '=': return deger === k.deger;
        case '!=': return deger !== k.deger;
        case '>': return deger > k.deger;
        case '<': return deger < k.deger;
        case 'icerir': return String(deger).includes(String(k.deger));
        case 'degisti': return true;
      }
    }
    return true;
  }

  private async aksiyonlariCalistir(kural: ITetiklemeKurali, kartVerisi: any): Promise<void> {
    for (const aksiyon of kural.aksiyonlar) {
      switch (aksiyon.tip) {
        case 'bildirim':
          await this.bildirimAksiyonu(aksiyon, kural, kartVerisi);
          break;
        case 'mail':
          await this.mailAksiyonu(aksiyon, kural);
          break;
        case 'push':
          await this.pushAksiyonu(aksiyon, kural);
          break;
        case 'webhook':
          await this.webhookAksiyonu(aksiyon, kartVerisi);
          break;
      }
    }
  }

  private async bildirimAksiyonu(aksiyon: ITetiklemeAksiyon, kural: ITetiklemeKurali, kartVerisi: any): Promise<void> {
    const { BildirimService } = await import('./bildirim.service');
    const bildirimSvc = this.injector.get(BildirimService);
    const mesaj = aksiyon.mesaj || `[${kural.ismi}] tetiklendi`;
    const hedefId = this.hedefIdBelirle(aksiyon, kartVerisi);
    if (hedefId) {
      await bildirimSvc.bildirimGonder(hedefId, 'atandi', kartVerisi?._id || '', kartVerisi?.baslik || '', mesaj, aksiyon.oncelik || 'normal');
    }
  }

  private async mailAksiyonu(aksiyon: ITetiklemeAksiyon, kural: ITetiklemeKurali): Promise<void> {
    const { FikoYTIstekKok } = await import('@/src/service/modules/fikoyt-istek-kok/fikoyt.istek.makinesi.service');
    const istek = new FikoYTIstekKok({ metod: 'POST', komut: 'FikoYT.sistem.mail.gonder' });
    const fd = new FormData();
    fd.append('req_body', JSON.stringify({
      konu: aksiyon.mailKonu || `[Tetikleme] ${kural.ismi}`,
      icerik: aksiyon.mailIcerik || kural.aciklama || '',
      hedefRol: aksiyon.hedefRol,
    }));
    istek.parametreler = fd;
    await istek.isteSync();
  }

  private async pushAksiyonu(aksiyon: ITetiklemeAksiyon, kural: ITetiklemeKurali): Promise<void> {
    const { FikoYTIstekKok } = await import('@/src/service/modules/fikoyt-istek-kok/fikoyt.istek.makinesi.service');
    const istek = new FikoYTIstekKok({ metod: 'POST', komut: 'FikoYT.sistem.push.gonder' });
    const fd = new FormData();
    fd.append('req_body', JSON.stringify({
      mesaj: aksiyon.mesaj || kural.ismi,
      oncelik: aksiyon.oncelik || 'normal',
      hedefRol: aksiyon.hedefRol,
    }));
    istek.parametreler = fd;
    await istek.isteSync();
  }

  private async webhookAksiyonu(aksiyon: ITetiklemeAksiyon, kartVerisi: any): Promise<void> {
    if (!aksiyon.webhookUrl) return;
    const metod = aksiyon.webhookMetod || 'POST';
    if (metod === 'GET') {
      await fetch(aksiyon.webhookUrl);
    } else {
      await fetch(aksiyon.webhookUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ kartVerisi, zaman: new Date().toISOString() }),
      });
    }
  }

  private hedefIdBelirle(aksiyon: ITetiklemeAksiyon, kartVerisi: any): string | null {
    switch (aksiyon.hedef) {
      case 'sahip': return kartVerisi?.sahipId || null;
      case 'olusturan': return kartVerisi?.olusturanId || null;
      case 'belirli_kullanici': return aksiyon.hedefKullaniciId || null;
      case 'rol_bazli': return null;
      default: return null;
    }
  }

  sablondanOlustur(sablonIndex: number): Partial<ITetiklemeKurali> {
    const s = HAZIR_SABLONLAR[sablonIndex];
    if (!s) return {};
    return { ...s, vt: VT };
  }
}
